'use client'

import clsx from 'clsx'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { AppliedFilterChip, useAppliedFilters } from '@/features/catalog/applied-filters'
import { useStepFilter } from '@/features/catalog/step-filter'
import { SubcategoryChips } from '@/features/catalog/subcategory-chips'

import { FilterProperties } from '@/entities/catalog-old'

import { TSection } from '@/shared/types/deprecated-types'
import { Icon } from '@/shared/ui/icon'
import { Skeleton } from '@/shared/ui/Skeleton'

import { getFoundLabel } from '../lib/plural-products'
import { useWheelScroll } from '../lib/use-wheel-scroll'

import cls from './guided-filter.module.scss'

export type GuidedFilterProps = {
	className?: string
	filterUrl: string
	onFilterChange?: (value: string) => void | Promise<void>
	// Sidebar filter data — step chips and applied chips are derived from it, not a separate fetch.
	properties?: FilterProperties[]
	isFilterLoading?: boolean
	// Total products in the current result — shown as "найдено X товаров" once all steps are picked.
	productCount?: number
	// Subcategories of the current category. When the category has no step filter at all (e.g. «Красота
	// и здоровье»), these replace the empty guided filter and are rendered as navigation chips.
	subCategories?: TSection[]
}

// Varied widths so the loading row reads as chips, not identical bars.
const SKELETON_CHIP_WIDTHS = [72, 104, 60, 88, 76]

// Unified "по шагам" guided filter laid out as two rows: the first holds the already-picked filters
// (reusing the old applied-filter chips, just relocated here) under "Выбрано:"; the second holds the
// single active step ("Выберите …" + its value chips). Once every step is picked the second row drops
// and a muted "найдено X товаров" appears at the right of the first row. "Сбросить всё" is the leading
// black × from the old applied-filters, shown from the second filter onward.
export const GuidedFilter: FC<GuidedFilterProps> = ({
	className,
	filterUrl,
	onFilterChange,
	properties,
	isFilterLoading,
	productCount,
	subCategories
}) => {
	const {
		activeStep,
		stepCodes,
		onSelectValue,
		isLoading: isStepLoading
	} = useStepFilter({
		filterUrl,
		onFilterChange,
		properties,
		isFilterLoading
	})

	const {
		blocks,
		toggleValue,
		deleteBlock,
		clearAll,
		isLoading: isAppliedLoading
	} = useAppliedFilters({
		onChange: value => onFilterChange?.(value),
		stepCodes,
		filterUrl
	})

	// Vertical mouse wheel → horizontal scroll on the mobile strips (no-op on the wrapped desktop rows).
	const pickedWheelRef = useWheelScroll<HTMLDivElement>()
	const stepWheelRef = useWheelScroll<HTMLDivElement>()

	// Lock the step chips the instant one is picked and hold through the whole apply, like the sidebar
	// inputs. isStepLoading alone isn't enough: it only flips true AFTER the sef-url round-trip, so
	// between the click and the refetch there's a frame where the chips re-enable. isPending bridges
	// that; a safety timeout releases it if no refetch runs (cached result) so it never sticks. Keyed
	// ONLY on the step/products loading — NOT the applied-filters query's isFetching, which flaps and
	// would otherwise grey out every chip on load.
	const [isPending, setIsPending] = useState(false)

	const handleSelect = (code: string, value: string | number): void => {
		setIsPending(true)
		onSelectValue(code, value)
	}

	useEffect(() => {
		if (!isPending) return undefined

		if (isStepLoading) {
			setIsPending(false)
			return undefined
		}

		const timer = setTimeout(() => setIsPending(false), 1500)
		return () => clearTimeout(timer)
	}, [isPending, isStepLoading])

	const isStepDisabled = isStepLoading || isPending

	const isBusy = isStepLoading || isAppliedLoading || isPending

	// Does the live selection (filterUrl) actually carry any filter? (LIST_SECTION_CODE is the category,
	// not a filter.) While a filter applies, the applied-filters query churns through a transient empty
	// `blocks` even though the selection is still set — rendering that would flash the "нет выбранных
	// фильтров" placeholder. selectionEmpty lets us tell a real clear ("Сбросить всё") from that artifact.
	const selectionEmpty = useMemo(() => {
		try {
			const parsed = JSON.parse(filterUrl || '{}') as Record<string, { value?: unknown[] }>
			return !Object.entries(parsed).some(
				([code, entry]) => code !== 'LIST_SECTION_CODE' && Array.isArray(entry?.value) && entry.value.length > 0
			)
		} catch {
			return true
		}
	}, [filterUrl])

	// A `blocks` value is trustworthy only when nothing is busy AND it's either non-empty or a genuine
	// clear — anything else (empty while a selection still exists) is a mid-apply artifact. We render the
	// trustworthy value, and otherwise freeze on the last trustworthy one. The ref is updated in an
	// effect (commit-only, concurrent-safe), never mutated during render, so discarded renders can't
	// corrupt it and a transient empty can never flash the "нет выбранных фильтров" placeholder.
	const blocksTrustworthy = !isBusy && (blocks.length > 0 || selectionEmpty)
	const lastGoodBlocksRef = useRef(blocks)
	useEffect(() => {
		if (blocksTrustworthy) lastGoodBlocksRef.current = blocks
	})
	const displayBlocks = blocksTrustworthy ? blocks : lastGoodBlocksRef.current

	// Hide values with no products (ELEMENT_COUNT === 0) — they can't be picked meaningfully.
	const values = activeStep?.VALUES?.filter(({ ELEMENT_COUNT }) => ELEMENT_COUNT > 0) ?? []
	const anyPicked = displayBlocks.length > 0
	const isColdLoad = isStepLoading && !activeStep && !anyPicked

	// Show the "нет выбранных фильтров" placeholder only when the selection is genuinely, stably empty:
	// never while anything is applying (isBusy) and never during the cold load. Combined with the frozen
	// displayBlocks above, this keeps the placeholder from flashing when filters are added/removed.
	const showPlaceholder = !anyPicked && selectionEmpty && !isBusy && !isColdLoad

	// Category with no step filter at all (no step codes resolved — e.g. «Красота и здоровье», whose
	// step-filter endpoint 404s) and nothing picked. The guided filter would just show the "нет
	// выбранных фильтров" placeholder, so surface the subcategories as navigation chips instead. Guard
	// on !isColdLoad so the step skeleton shows first and this never flashes before steps resolve.
	const showSubcategories = !stepCodes.length && !anyPicked && !isColdLoad && !!subCategories?.length
	if (showSubcategories) {
		return <SubcategoryChips className={className} items={subCategories ?? []} />
	}

	// Both rows always render (reserve height) so the block never collapses or jumps — on the last step,
	// during a cold load, or while a filter applies. Row 1 shows the picked chips, the "нет выбранных
	// фильтров" placeholder, or (briefly, mid-apply) nothing — see showPlaceholder.
	return (
		<div className={clsx(cls.root, className)}>
			{/* Row 1 — picked filters: the chips when something is picked, else the "нет выбранных фильтров"
			    placeholder once the selection is stably empty. During an apply it holds the frozen chips or
			    renders empty, never flashing the placeholder. */}
			<div className={cls.rowPicked}>
				{anyPicked ? (
					<>
						<span className={cls.lead}>Выбрано:</span>
						{/* Only the chips scroll (with the scrollbar under them) — the lead label stays put. */}
						<div ref={pickedWheelRef} className={cls.scroller}>
							{/* Leading "Сбросить всё" — the old black × clear-all, shown from the second filter on. */}
							{displayBlocks.length > 1 && (
								<button
									type="button"
									className={cls.clearAll}
									onClick={clearAll}
									disabled={isBusy}
									aria-label="Сбросить все фильтры">
									<Icon name="Close" size={16} color="#fff" />
								</button>
							)}
							{displayBlocks.map(block => (
								<AppliedFilterChip
									key={block.code}
									block={block}
									isDisabled={isBusy}
									onToggleValue={toggleValue}
									onDelete={deleteBlock}
								/>
							))}
						</div>
					</>
				) : showPlaceholder ? (
					<span className={cls.placeholder}>нет выбранных фильтров</span>
				) : null}
			</div>

			{/* Row 2 — the active step, or a placeholder with the result count once steps are exhausted
			    (or the cold-load skeleton). Always rendered. */}
			<div className={cls.rowStep}>
				{activeStep && values.length > 1 ? (
					<>
						<span className={cls.ask}>{activeStep.NAME_HINT}</span>
						{/* Only the chips scroll (with the scrollbar under them) — the "Выберите …" label stays put. */}
						<div ref={stepWheelRef} className={cls.scroller}>
							{values.map(({ ID, NAME, VALUE }) => (
								<button
									key={ID}
									type="button"
									className={cls.chip}
									disabled={isStepDisabled}
									onClick={() => handleSelect(activeStep.CODE, VALUE)}>
									{NAME ?? String(VALUE)}
								</button>
							))}
						</div>
					</>
				) : isColdLoad ? (
					<>
						<Skeleton width={120} height={16} borderRadius={4} />
						<div className={cls.scroller}>
							{SKELETON_CHIP_WIDTHS.map((width, index) => (
								<Skeleton
									key={`chip-skeleton-${index}`}
									className={cls.chipSkeleton}
									width={width}
									height={32}
									borderRadius={8}
								/>
							))}
						</div>
					</>
				) : anyPicked && typeof productCount === 'number' ? (
					// Filters picked but no suggestions left → show the result count in the step row.
					<span className={cls.found}>{getFoundLabel(productCount)}</span>
				) : null}
			</div>
		</div>
	)
}
